import { CartesianGrid, ComposedChart, Line, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartTooltip } from "@/components/common/ChartTooltip";
import { axisTick, chartTheme } from "@/lib/chartTheme";
import { fmtNum, osloHour } from "@/lib/format";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const hh = (h) => String(h).padStart(2, "0");

/** Direct label on the latest measurement: dot + "78,9 MW". */
const EndLabel = ({ cx, cy, value, color, flip, ring }) => {
  if (cx === undefined || cy === undefined) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r="4.5" fill={color} stroke={ring} strokeWidth="2" />
      <text x={flip ? cx + 4 : cx + 11} y={flip ? cy - 11 : cy + 4.5} textAnchor={flip ? "end" : "start"} fontSize="13" fontWeight="600" fill={color} style={{ fontVariantNumeric: "tabular-nums" }}>
        {`${fmtNum(value, 1)} MW`}
      </text>
    </g>
  );
};

/** A readable y-range for an operations line chart: tight around the data, 5 MW steps, never below 0. */
const niceDomain = (values, capacity) => {
  if (!values.length) return [0, capacity ? Math.ceil(capacity * 1.1) : 10];
  const lo = Math.min(...values);
  const hi = Math.max(...values, capacity || 0);
  const step = hi - lo > 40 ? 10 : hi - lo > 12 ? 5 : hi - lo > 4 ? 1 : 0.5;
  const pad = Math.max((hi - lo) * 0.25, step);
  const min = Math.max(0, Math.floor((lo - pad) / step) * step);
  const max = Math.ceil((hi + pad * 0.5) / step) * step;
  return [min, max === min ? min + step : max];
};

/**
 * "Effekt i dag": measured power per hour for today (00 → latest measurement) against yesterday as a thin grey line.
 * The empty right-hand part of the day axis makes it self-evident that the day is not finished.
 * rows: [{ts, power_mw}] – timestamps are mapped to Norwegian local hours.
 */
export const TodayPowerChart = ({ today = [], yesterday = [], capacity, height = 240, isDark = false, testId }) => {
  const t = chartTheme(isDark);
  const byHour = (rows) => {
    const m = {};
    rows.forEach((r) => {
      m[osloHour(r.ts)] = r.power_mw;
    });
    return m;
  };
  const tm = byHour(today);
  const ym = byHour(yesterday);
  const data = HOURS.map((h) => ({ h, today: tm[h] ?? null, yesterday: ym[h] ?? null }));
  const last = [...data].reverse().find((d) => d.today !== null && d.today !== undefined);
  const values = data.flatMap((d) => [d.today, d.yesterday]).filter((v) => v !== null && v !== undefined);
  const domain = niceDomain(values, capacity);
  const ring = isDark ? "hsl(210 28% 6%)" : "#fff";
  const decimals = domain[1] - domain[0] <= 5 ? 1 : 0;

  return (
    <div style={{ height }} data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 76, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={t.grid} />
          <XAxis dataKey="h" type="number" domain={[0, 23]} ticks={[0, 3, 6, 9, 12, 15, 18, 21]} tickFormatter={hh} tick={axisTick(t)} tickLine={false} axisLine={false} tickMargin={10} />
          <YAxis domain={domain} tickCount={6} allowDecimals={decimals > 0} tick={axisTick(t)} tickLine={false} axisLine={false} width={42} tickFormatter={(v) => fmtNum(v, decimals)} />
          <Tooltip cursor={{ stroke: t.cursor, strokeOpacity: 0.2, strokeDasharray: "3 3" }} content={<ChartTooltip formatter={(v) => `${fmtNum(v, 1)} MW`} labelFormatter={(h) => `kl. ${hh(h)}.00`} />} />
          {capacity ? <ReferenceLine y={capacity} stroke={t.faint} strokeDasharray="3 5" ifOverflow="extendDomain" label={{ value: `Installert ${fmtNum(capacity, 1)} MW`, position: "insideTopRight", fill: t.tick, fontSize: 12, dy: -4 }} /> : null}
          <Line type="monotone" dataKey="yesterday" name="I går" stroke={t.faint} strokeWidth={1.5} dot={false} activeDot={{ r: 3, fill: t.faint, strokeWidth: 0 }} connectNulls={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="today" name="I dag" stroke={t.primary} strokeWidth={2.25} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: ring, fill: t.primary }} connectNulls={false} isAnimationActive={false} />
          {last ? <ReferenceDot x={last.h} y={last.today} r={0} shape={(p) => <EndLabel {...p} value={last.today} color={t.primary} ring={ring} flip={last.h >= 21} />} ifOverflow="visible" /> : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
